import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(sessionStorage.getItem('accessToken')); // Changed to sessionStorage
  const [refreshToken, setRefreshToken] = useState(sessionStorage.getItem('refreshToken'));
  const [loading, setLoading] = useState(true);
  const [setupComplete, setSetupComplete] = useState(null);

  const saveTokens = useCallback((access, refresh) => {
    setToken(access);
    setRefreshToken(refresh);
    if (access) sessionStorage.setItem('accessToken', access);
    else sessionStorage.removeItem('accessToken');
    if (refresh) sessionStorage.setItem('refreshToken', refresh);
    else sessionStorage.removeItem('refreshToken');
  }, []);

  const logout = useCallback(() => {
    if (refreshToken) {
      api.authRequest('/api/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      }, token).catch(() => {});
    }
    setUser(null);
    saveTokens(null, null);
  }, [refreshToken, token, saveTokens]);

  const refreshAccessToken = useCallback(async () => {
    if (!refreshToken) return false;
    try {
      const res = await fetch(`${api.baseUrl}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) {
        logout();
        return false;
      }
      const data = await res.json();
      setUser(data.user);
      saveTokens(data.accessToken, data.refreshToken);
      return true;
    } catch {
      logout();
      return false;
    }
  }, [refreshToken, logout, saveTokens]);

  // Check initial state
  useEffect(() => {
    const init = async () => {
      try {
        const status = await api.get('/api/auth/status');
        setSetupComplete(status.setup_complete);

        if (token) {
          try {
            const data = await api.authRequest('/api/auth/me', {}, token);
            const res = await data;
            setUser(res.user);
          } catch {
            // Token expired, try refresh
            const ok = await refreshAccessToken();
            if (!ok) setUser(null);
          }
        }
      } catch (e) {
        console.error('Auth init error:', e);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [token, refreshAccessToken]); // Added dependencies

  const login = useCallback(async (email, password) => {
    const data = await api.post('/api/auth/login', { email, password });
    setUser(data.user);
    saveTokens(data.accessToken, data.refreshToken);
    setSetupComplete(true);
    return data;
  }, [saveTokens]);

  const setup = useCallback(async (email, password, displayName) => {
    const data = await api.post('/api/auth/setup', { email, password, display_name: displayName });
    setUser(data.user);
    saveTokens(data.accessToken, data.refreshToken);
    setSetupComplete(true);
    return data;
  }, [saveTokens]);

  // Provide an authed fetch helper
  const authedFetch = useCallback(async (path, options = {}) => {
    const doFetch = async (accessToken) => {
      const url = `${api.baseUrl}${path}`;
      const config = {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          ...options.headers,
        },
      };
      const res = await fetch(url, config);
      return res;
    };

    let res = await doFetch(token);
    if (res.status === 401 && refreshToken) {
      const ok = await refreshAccessToken();
      if (ok) {
        const newToken = sessionStorage.getItem('accessToken');
        res = await doFetch(newToken);
      }
    }

    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(err.detail || err.message || `Request failed: ${res.status}`);
    }

    const contentType = res.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return res.json();
    }
    return res;
  }, [token, refreshToken, refreshAccessToken]);

  const value = {
    user,
    token,
    loading,
    setupComplete,
    isAdmin: user?.role === 'admin',
    isEditor: user?.role === 'admin' || user?.role === 'editor',
    isViewer: user?.role === 'viewer',
    login,
    setup,
    logout,
    authedFetch,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
